import { useState, useEffect, useMemo } from 'react';
import getContent from 'lib/getContent';
import { page } from 'lib/track';

/**
 * Hook to fetch and process Action Hour data structure
 * @param {string} slug - The content slug
 * @param {string} product - The product identifier
 * @param {string} authToken - Authentication token
 * @param {Object} user - User object with UUID
 * @returns {Object} Processed data with video, downloads, bio information, loading and error states
 */
export const useActionHourData = (slug, product, authToken) => {
  const [contentItem, setContentItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActionHour = async () => {
      console.log('fetchActionHour', { slug, product, authToken });
      try {
        setLoading(true);
        const res = await getContent(slug, product, authToken);
        setContentItem(res);
      } catch (e) {
        console.log('error getting Action Hour data', e.message);
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    console.log('fetchActionHour', { slug, product, authToken });
    if (slug && product && authToken) {
      fetchActionHour();
    }
  }, [slug, product, authToken]);

  const processedData = useMemo(() => {
    if (!contentItem || !contentItem.Children) {
      return {
        hasData: false,
        videoData: null,
        videoDescription: null,
        downloads: [],
        bioData: null,
        bioDescription: null,
        bioTitle: null,
        bioImage: null,
        userAnalytics: null
      };
    }

    const { Children = [], childrenIndexes = {} } = contentItem;

    // Extract video data (first video child)
    const videoIndex = childrenIndexes.video?.[0] ?? 0;
    const videoData = Children[videoIndex];
    const videoDescription = videoData?.displayProps?.description || videoData?.data?.description;

    // Extract downloads
    const downloadIndexes = childrenIndexes.download || [];
    const downloads = downloadIndexes.map(idx => Children[idx]).filter(Boolean);

    // Extract bio
    const bioIndex = childrenIndexes.bio?.[0];
    const bioData = bioIndex !== undefined ? Children[bioIndex] : null;
    const bioDescription = bioData?.displayProps?.description || bioData?.data?.description;
    const bioTitle = bioData?.displayProps?.title || bioData?.title;
    const bioImage = bioData?.displayProps?.image?.url || bioData?.data?.img;

    return {
      hasData: true,
      videoData,
      videoDescription,
      downloads,
      bioData,
      bioDescription,
      bioTitle,
      bioImage,
      contentItem
    };
  }, [contentItem]);

  return {
    ...processedData,
    loading,
    error
  };
};
